import React, { useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  CalendarDays,
  CheckCircle2,
  Clipboard,
  Database,
  Edit3,
  Flame,
  Home,
  Image as ImageIcon,
  Loader2,
  MessageCircle,
  MoreVertical,
  RadioTower,
  Settings,
  Sparkles,
  UserRound,
  Video
} from "lucide-react";
import "./styles.css";

type PlatformId = "xiaohongshu" | "douyin" | "wechat" | "weibo";

type Trend = {
  rank: number;
  tag: string;
  title: string;
  summary: string;
  suggestion: string;
  heat: string;
};

type PlatformPost = {
  platform: PlatformId;
  title: string;
  body: string;
  hashtags: string[];
  imagePrompt: string;
  imageUrl?: string;
  status: string;
};

type GenerationResult = {
  ok: boolean;
  provider: string;
  model: string;
  date: string;
  topic: string;
  posts: PlatformPost[];
  trends: Trend[];
  error?: string;
};

const platformMeta: Record<PlatformId, { name: string; icon: React.ElementType; color: string }> = {
  xiaohongshu: { name: "小红书", icon: Clipboard, color: "#ff2442" },
  douyin: { name: "抖音", icon: Video, color: "#111827" },
  wechat: { name: "公众号", icon: MessageCircle, color: "#16a34a" },
  weibo: { name: "微博", icon: RadioTower, color: "#f59e0b" }
};

const seedTrends: Trend[] = [
  { rank: 1, tag: "宠物", title: "#夏天到了宠物防暑降温", summary: "夏季高温来袭，宠物防暑护理成为养宠人关注点。", suggestion: "建议方向：鹦鹉降温 / 饮水 / 通风", heat: "96.3w" },
  { rank: 2, tag: "鹦鹉", title: "#玄凤鹦鹉说话合集", summary: "玄凤学说话视频持续走红，适合做轻松互动内容。", suggestion: "建议方向：说话训练 / 互动技巧", heat: "82.7w" },
  { rank: 3, tag: "社会", title: "#高考结束中高考后放松指南", summary: "高考结束后，家庭陪伴与治愈话题升温。", suggestion: "建议方向：鹦鹉陪伴 / 解压日常", heat: "78.4w" },
  { rank: 4, tag: "宠物", title: "#鹦鹉掉毛怎么办", summary: "换羽季掉毛问题受关注，新手需要判断和护理方法。", suggestion: "建议方向：换羽护理 / 营养补充", heat: "65.1w" },
  { rank: 5, tag: "宠物", title: "#宠物友好出行攻略", summary: "暑假出行计划带动宠物出行话题。", suggestion: "建议方向：鹦鹉出行 / 安全注意", heat: "52.6w" }
];

const seedPosts: PlatformPost[] = [
  {
    platform: "xiaohongshu",
    title: "夏天养鹦鹉的 5 个降温小技巧，新手必看",
    body: "夏天到了，鹦鹉也怕热。分享 5 个我亲测有效的降温方法，帮它安全度夏。",
    hashtags: ["鹦鹉降温", "新手养鹦鹉", "鹦鹉夏天护理", "科学养宠"],
    imagePrompt: "清爽自然光，一只玄凤鹦鹉站在水盆旁，封面文字：鹦鹉夏天怎么降温",
    status: "已生成"
  },
  {
    platform: "douyin",
    title: "鹦鹉太热了怎么办？5 招让它凉快一夏",
    body: "开场：你家鹦鹉夏天有没有张嘴喘气？接着讲通风、饮水、洗澡、遮阳和观察状态。",
    hashtags: ["鹦鹉降温", "养鹦鹉新手", "宠物知识", "鹦鹉日常"],
    imagePrompt: "竖版短视频封面，鹦鹉和大字标题，清爽夏日水雾感",
    status: "已生成"
  },
  {
    platform: "wechat",
    title: "夏天如何科学给鹦鹉降温？这份详细指南请收好",
    body: "从环境、饮水、洗澡、饮食和异常表现 5 个角度，整理一份适合新手的夏季护理指南。",
    hashtags: ["鹦鹉护理", "科学养宠", "夏季养护"],
    imagePrompt: "公众号首图，白色鹦鹉站在绿植旁，标题排版干净清晰",
    status: "已生成"
  },
  {
    platform: "weibo",
    title: "鹦鹉也怕热，今天记得检查这 5 件事",
    body: "通风、饮水、避免暴晒、观察喘气、别乱吹空调。你家鹦鹉夏天最爱哪种降温方式？",
    hashtags: ["鹦鹉降温", "养宠经验", "鹦鹉日常"],
    imagePrompt: "九宫格风格：鹦鹉喝水、站栖木、洗澡、绿植背景",
    status: "已生成"
  }
];

function todayLabel() {
  return new Intl.DateTimeFormat("zh-CN", { year: "numeric", month: "long", day: "numeric", weekday: "short" }).format(new Date());
}

function App() {
  const [result, setResult] = useState<GenerationResult>({
    ok: true,
    provider: "rules",
    model: "seed",
    date: todayLabel(),
    topic: "夏季鹦鹉护理",
    posts: seedPosts,
    trends: seedTrends
  });
  const [positioning, setPositioning] = useState("科学养鹦鹉的生活记录。面向新手鹦鹉主人，口语、温暖、专业，分享实用养护知识和日常陪伴。");
  const [loading, setLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState<Record<string, boolean>>({});
  const generatedCount = useMemo(() => result.posts.filter((post) => post.status).length, [result.posts]);

  async function generateDaily() {
    if (loading) return;
    setLoading(true);
    try {
      const response = await fetch("/api/daily", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ positioning, tone: "口语", platformsPerDay: 1 })
      });
      setResult((await response.json()) as GenerationResult);
    } catch (error) {
      setResult((current) => ({ ...current, ok: false, provider: "fallback", model: "rules", error: error instanceof Error ? error.message : "network error" }));
    } finally {
      setLoading(false);
    }
  }

  async function generateImage(post: PlatformPost) {
    if (imageLoading[post.platform]) return;
    setImageLoading((current) => ({ ...current, [post.platform]: true }));
    try {
      const response = await fetch("/api/image", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ platform: post.platform, prompt: post.imagePrompt, title: post.title })
      });
      const data = await response.json();
      if (!data.ok || !data.imageUrl) throw new Error(data.error || "image generation failed");
      setResult((current) => ({
        ...current,
        posts: current.posts.map((item) => item.platform === post.platform ? { ...item, imageUrl: data.imageUrl } : item)
      }));
    } catch (error) {
      setResult((current) => ({
        ...current,
        error: error instanceof Error ? error.message : "image generation failed"
      }));
    } finally {
      setImageLoading((current) => ({ ...current, [post.platform]: false }));
    }
  }

  return (
    <main className="app-shell">
      <aside className="sidebar">
        <a className="brand" href="#top">
          <span className="brand-mark">鹦</span>
          <div><strong>鹦鹉内容日历</strong><small>每天一条，省时省心</small></div>
        </a>
        <nav className="main-nav" aria-label="主导航">
          {[[Home, "工作台", true], [CalendarDays, "内容日历"], [Database, "内容库"], [Flame, "热点雷达"], [ImageIcon, "素材库"], [UserRound, "账号定位"], [Settings, "设置"]].map(([Icon, label, active]) => (
            <a className={active ? "active" : ""} href="#top" key={String(label)}>
              {React.createElement(Icon as React.ElementType, { size: 18 })}{label as string}
            </a>
          ))}
        </nav>
        <section className="position-card">
          <div><strong>账号定位概览</strong><button>编辑</button></div>
          <dl>
            <dt>定位</dt><dd>科学养鹦鹉的生活记录</dd>
            <dt>受众</dt><dd>新手鹦鹉主人 / 18-35 岁</dd>
            <dt>风格</dt><dd>口语、温暖、专业、实用</dd>
          </dl>
        </section>
      </aside>

      <section className="workspace" id="top">
        <header className="topbar">
          <div><h1>{result.date}</h1><p>坚持记录，陪鹦鹉健康快乐每一天</p></div>
          <button className="generate-button" onClick={generateDaily} disabled={loading}>
            {loading ? <Loader2 className="spin" size={18} /> : <Sparkles size={18} />}生成今日内容
          </button>
          <div className="status-row">
            <span><CheckCircle2 size={18} />已生成 {generatedCount}/4</span><span>待发布 0</span><span>草稿箱 0</span>
          </div>
        </header>

        <div className="grid">
          <section className="trends-panel">
            <div className="panel-head"><h2>今日热点雷达</h2><span>06-02 08:30 更新</span></div>
            <div className="trend-list">
              {result.trends.map((trend) => (
                <article className="trend-card" key={trend.rank}>
                  <div><b>{trend.rank}</b><small>{trend.tag}</small><em><Flame size={14} />{trend.heat}</em></div>
                  <h3>{trend.title}</h3><p>{trend.summary}</p><span>{trend.suggestion}</span>
                </article>
              ))}
            </div>
            <p className="source-note">数据来源：公开热点 + 鹦鹉赛道规则筛选；弱数据时自动使用本地缓存。</p>
          </section>

          <section className="posts-panel">
            {result.posts.map((post) => {
              const meta = platformMeta[post.platform];
              const Icon = meta.icon;
              return (
                <article className="post-card" key={post.platform}>
                  <div className="post-head">
                    <span className="platform-icon" style={{ background: meta.color }}><Icon size={18} /></span>
                    <strong>{meta.name}</strong>
                    <mark><CheckCircle2 size={14} />{post.status || "已生成"}</mark>
                    <button><Edit3 size={15} />编辑</button>
                    <MoreVertical size={18} />
                  </div>
                  <div className="post-body">
                    <dl>
                      <dt>标题</dt><dd>{post.title}</dd>
                      <dt>正文</dt><dd>{post.body}</dd>
                      <dt>话题</dt><dd>{post.hashtags.map((tag) => `#${tag}`).join("  ")}</dd>
                      <dt>配图方向</dt><dd>{post.imagePrompt}</dd>
                    </dl>
                    <div className={post.imageUrl ? "image-brief has-image" : "image-brief"}>
                      {post.imageUrl ? (
                        <img src={post.imageUrl} alt={`${meta.name} 配图`} />
                      ) : (
                        <>
                          <ImageIcon size={28} />
                          <span>MiniMax 配图</span>
                        </>
                      )}
                      <button onClick={() => generateImage(post)} disabled={imageLoading[post.platform]}>
                        {imageLoading[post.platform] ? <Loader2 className="spin" size={14} /> : <Sparkles size={14} />}
                        {post.imageUrl ? "重新生成" : "生成配图"}
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </section>

          <aside className="inspector">
            <div className="panel-head"><h2>账号定位</h2><button>编辑</button></div>
            <label>表达风格<input value="口语" readOnly /></label>
            <label>受众画像<textarea value={positioning} onChange={(event) => setPositioning(event.target.value)} /></label>
            <section><h3>内容调性</h3><div className="chip-row">{["温暖治愈", "专业可靠", "实用干货", "轻松有趣"].map((item) => <span key={item}>{item}</span>)}</div></section>
            <section><h3>禁用词与话题</h3><div className="danger-row">{["绝对化用语", "夸大效果", "包治百病", "政治敏感", "低俗"].map((item) => <span key={item}>{item}</span>)}</div></section>
            <section><h3>图片风格偏好</h3><div className="style-grid"><button className="active">清新自然</button><button>温暖治愈</button><button>简约干净</button></div></section>
            <section className="limits"><h3>每平台字数建议</h3><p>小红书：500-800 字；抖音：口播 + 标题不超过 30 字；公众号：1200-1800 字；微博：140 字内。</p></section>
            {result.error && <p className="error">当前使用备用生成：{result.error}</p>}
            <p className="provider">生成引擎：{result.provider} / {result.model}</p>
          </aside>
        </div>
      </section>
    </main>
  );
}

createRoot(document.getElementById("root")!).render(<App />);
